// Per-plan token bonus for the heart pill, matching the platform's formula:
//   bonus = (GB premium-tokens-amount || settings.monthly_tokens_bonus)
//         + instant[duration] (GB premium-instant-tokens-amount) + plan.tokensExtraBonus
import { useEffect, useState } from 'react';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

// Parse a "X|Y|Z" instant-tokens string into the platform's duration map.
// Mirrors calculateTokensExtraBonus: index 0→1mo, 1→3mo, 2→12mo.
const parseInstantTokens = (instantTokens) => {
  const [oneMonth, threeMonth, twelveMonth] = (instantTokens || '0|0|0')
    .split('|')
    .map((v) => Number(v) || 0);
  return { 1: oneMonth, 3: threeMonth, 12: twelveMonth };
};

// instantTokensOverride: force "1mo|3mo|12mo" instead of reading the GB flag.
export function useTokenBonus({ instantTokensOverride } = {}) {
  // Read the STICKY-resolved variants from GrowthBookContext instead of calling
  // useFeatureValue directly. On the Google OAuth return the popup mounts before
  // GrowthBook re-resolves the experiment, so a live read yields '' and the pill
  // would flash the settings/hardcoded 200 fallback. The context persists the
  // variant resolved on load A (sessionStorage) and rehydrates it on the return,
  // so the popup shows on first paint exactly the variant that was sent in
  // ab_testing and stored in marketing.experiments (single source for both).
  const { premiumTokensAmount, premiumInstantTokens } = useGrowthBookValues();
  const gbMonthlyTokens = premiumTokensAmount;
  const gbInstantTokens = premiumInstantTokens;

  // Settings fallback for the monthly bonus (used only if the GB flag is empty),
  // mirroring the platform's `Number(gbMonthlyTokens || monthlyTokens)`.
  const [settingsMonthlyBonus, setSettingsMonthlyBonus] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/public-configs/system-settings`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': API_KEY,
            },
          },
        );
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && typeof data?.monthly_tokens_bonus === 'number') {
          setSettingsMonthlyBonus(data.monthly_tokens_bonus);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[paywall-popup] system-settings fetch failed', err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // GB monthly wins; fall back to the settings value (platform precedence).
  const monthlyTokensBonus = Number(gbMonthlyTokens) || settingsMonthlyBonus;
  const instantByDuration = parseInstantTokens(
    instantTokensOverride ?? gbInstantTokens,
  );

  const getBonusForPlan = (plan) => {
    const instant = instantByDuration[plan?.duration_months] || 0;
    return monthlyTokensBonus + instant + (plan?.tokensExtraBonus ?? 0);
  };

  return { getBonusForPlan, monthlyTokensBonus, isLoading };
}
